import { Button, message } from "antd";
import dayjs from "dayjs";
import { ErrorMessage, Form, Formik } from "formik";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import useGetData from "../../api/useGetData";
import DatePickerInput from "../../common/DatePickerInput";
import SelectInput from "../../common/SelectInput";
import TimePicker from "../../common/TimePicker";
import { isPointInPolygon } from "../../utils/mapUtils";
import { CityTripRideDetailsValidation } from "../../utils/validations";
import MapModal from "../MapModal/MapModal";
import AgentUserSelect from "../UsersSelect";
import { CityTripContext } from "./CityTripContext";
import { bookingTracking } from "../../utils/mixpanel";
import usePostData from "../../api/usePostData";

const service = "City Trip";

/**
 * The Ride details step of the city ride trip
 *
 * It shows a form containing the following:
 *  - In the case of agent, the user whom the trip is intended for
 *  - The city the trip is intended to be in. The options for this comes from backend.
 *  - The date the ride is intended to be in.
 *  - The time the ride is intended to be in. The combination of date and time can't be in the past logically, and also can't be before a buffer duration (call `bookingAllowedTime`) decided from the backend
 *  - The location/destination on map. it operates depending on certain allowed zones that come from the backend. Whether the selected location on map is intended as pick up or drop off depend on the selected ride type
 *
 * @returns {JSX.Element}
 */

const RideDetailsStep = () => {
  const { t } = useTranslation("global");
  const bookingSettings = useSelector(
    (state) => state.cachedRequests.bookingSettings
  );
  const [mapModalOpen, setMapModalOpoen] = useState(false);
  const user = useSelector((state) => state.authData.user);
  const isRegistered = !!user;
  const { setActiveTab, firstStepValues, setFirstStepValues, setErrorMessage, errorMessage } =
    useContext(CityTripContext);
  const { data: cities } = useGetData({
    route: "api/method/airport_transport.api.bookings.get_city",
    params: {
      service,
    },
  });
  const { data: zonesData, getData: getZonesData } = useGetData({ route: "api/method/airport_transport.api.bookings.get_zones", notLoadData: true, });
  const { postData: checkTripBookingTime } = usePostData();
  useEffect(() => {
    if (firstStepValues.arrivalCity) {
      getZonesData({
        params: {
          city: firstStepValues.arrivalCity,
          service,
        },
      });
    }
  }, [firstStepValues]);
  const bookingAllowedTime = bookingSettings.booking_allowed_time / 60;
  const resetPickup = (setFieldValue) => {
    setFieldValue("location", "");
    setFieldValue("locationCoords", null);
  };
  const resetDropoff = (setFieldValue) => {
    setFieldValue("destination", "");
    setFieldValue("destinationCoords", null);
  };
// _________________________ C H E C K I N I T I A L  B O O K I N G ______________________________//
  const Check_Trip_Exist_Before = async (date, time) => {
    try {
      const response = await checkTripBookingTime({
        route: "api/method/airport_transport.api.bookings.check_booking",
        data: {arrival_date:date.format("YYYY-MM-DD"), arrival_time: time.format("HH:mm")},
        customError: true
      });
      if (response.status !== 200) {
        if(response?.error?.includes("You already have")){
          message.error(t("have_a_trip_for_this_date_and_time")); 
          setErrorMessage(response?.error);
        }
      }
      else{
        return true;
      }
    } catch (error) {
      message.error("Booking error occurred");
      return false;
    }
  };
// _______________________________________________________________________________________________//

  const onSubmit = async (values) => {
   if(isRegistered){
      const bookingTripInitially = await Check_Trip_Exist_Before(values.arrivalDate, values.arrivalTime);
        if (!bookingTripInitially) {
          return; // Stop execution if inializing booking fails
        }
    }
    const zone = (
      zonesData.find((zone) =>
        isPointInPolygon(values.destinationCoords, zone.map)
      ) || zonesData?.[0]
    )?.name;
    setFirstStepValues({
      agentUser: values.agentUser,
      arrivalCity: values.arrivalCity,
      arrivalDate: values.arrivalDate,
      arrivalTime: values.arrivalTime,
      location: values.location,
      destination: values.destination,
      locationCoords: values.locationCoords,
      destinationCoords: values.destinationCoords,
      zone,
    });
    bookingTracking({
      user: user?.email,
      service: "City",
      step: "Ride Details Step",
    });
    setActiveTab(2);
  };
  return (
    <div>
      <Formik
        initialValues={{
          agentUser: firstStepValues.agentUser,
          arrivalCity: firstStepValues.arrivalCity,
          arrivalDate:
            firstStepValues.arrivalDate ||
            dayjs().add(bookingAllowedTime, "hours"),
          arrivalTime:
            firstStepValues.arrivalTime ||
            dayjs().add(bookingAllowedTime, "hours"),
          location: firstStepValues.location,
          destination: firstStepValues.destination,
          locationCoords: firstStepValues.locationCoords,
          destinationCoords: firstStepValues.destinationCoords,
        }}
        onSubmit={onSubmit}
        validationSchema={CityTripRideDetailsValidation(
          t,
          bookingAllowedTime,
          user?.agent
        )}
      >
        {({ setFieldValue, values, errors, touched, setTouched }) => (
          <Form className="flex flex-col gap-4">
            {user?.agent && (
              <AgentUserSelect
                label={t("user_text")}
                name={"agentUser"}
                onChange={(user) => {
                  setFieldValue("agentUser", user);
                }}
                placeholder={t("hero.select_user")}
              />
            )}
            <SelectInput
              name="arrivalCity"
              label={t("hero.arrival_city_text")}
              placeholder={t("hero.select_arrival_city")}
              options={cities?.map((c) => ({ value: c, label: c }))}
              onChange={(city) => {
                getZonesData({
                  params: {
                    city,
                    service,
                  },
                });
                setFieldValue("terminalNumber", "");
                setFieldValue("location", "");
                resetPickup(setFieldValue);
                resetDropoff(setFieldValue);
                setFieldValue("airportName", "");
                setFieldValue("arrivalCity", city);
              }}
            />
            <div className="flex flex-col gap-1">
              <label htmlFor="arrivalDate">{t("hero.arrival_date_text")}</label>
              <DatePickerInput
                format={"YYYY-MM-DD"}
                value={values.arrivalDate}
                onChange={(date) => {
                  setFieldValue("arrivalDate", date);
                  setErrorMessage("");
                }}
                name="arrivalDate"
                disabledDate={(current) => {
                  return (
                    current &&
                    current <
                      dayjs().add(bookingAllowedTime, "hours").startOf("day")
                  );
                }}
              />
              {errors.arrivalDate && touched.arrivalDate && (
                <p className="text-text_warning text-sm mt-1 text-start">
                  {t(errors.arrivalDate)}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="arrivalDate">{t("hero.arrival_time_text")}</label>
              <TimePicker
                value={values.arrivalTime}
                onChange={(time) => {
                  setFieldValue("arrivalTime", time);
                  setErrorMessage("");
                }}
                name="arrivalTime"
              />
              <ErrorMessage name="arrivalTime" render={renderErrorMessage} />
            </div>
            {errorMessage && <p className="font-[500] text-text_warning text-[15px] mt-1 text-start">{t("have_a_trip_for_this_date_and_time")}</p>}
            <div className="flex flex-col gap-1">
              <button
                onClick={() => {
                  setMapModalOpoen(true);
                  setTouched("location", true);
                  setTouched("destination", true);
                }}
                className="flex justify-between w-full bg-background_white text-gray-700 border border-border_color font-medium rounded-lg text-sm px-3 py-3 text-left disabled:bg-background_grey"
                type="button"
                disabled={!values.arrivalCity}
              >
                <span>{t("hero.place_of_arrival_text")}</span>
                <img src="./assets/map/map.png" alt="map" />
              </button>
              {touched.location &&
                touched.destination &&
                (errors.location || errors.destination) && (
                  <span
                    style={{
                      color: "#ff4d4f",
                      fontSize: "small",
                    }}
                  >
                    {t(errors.location) || t(errors.destination)}
                  </span>
                )}
            </div>
            {mapModalOpen && (
              <MapModal
                cityName={values.arrivalCity}
                closeModal={() => {
                  setMapModalOpoen(false);
                }}
                rideName="cityTrip"
                isModalOpen={mapModalOpen}
                formValues={values}
                confirm={({
                  location,
                  locationCoords,
                  destination,
                  destinationCoords,
                }) => {
                  setFieldValue("location", location);
                  setFieldValue("locationCoords", locationCoords);
                  setFieldValue("destination", destination);
                  setFieldValue("destinationCoords", destinationCoords);
                }}
                zoneCoords={zonesData}
              />
            )}
            <Button
              className="bg-background_steel_blue w-full text-text_white hover:bg-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 mt-3"
              type="button"
              htmlType="submit"
              disabled={mapModalOpen}
            >
              {t("next_text")}
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

const renderErrorMessage = (msg) => (
  <span
    style={{
      color: "#ff4d4f",
      fontSize: "small",
    }}
  >
    {msg}
  </span>
);

export default RideDetailsStep;
